import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import useFetch from '../../api/useFetch';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../global/theme/tokens';
import TypeMoney from '../form/form-fields/TypeMoney';
import TypeText from '../form/form-fields/TypeText';
import { updateOrCreate } from '../special/updateOrCreate';
import { ProjectAddElementButton } from '../exo/project/ProjectAddElementButton';
import ExoPositions from '../exo/ExoPositions';
import { PositionImage } from '../exo/positions/collapse-modules/PositionImage';
import { PositionDescription } from '../exo/positions/collapse-modules/PositionDescription';
import { PositionNumber } from '../exo/positions/collapse-modules/PositionNumber';
import { PositionText } from '../exo/positions/collapse-modules/PositionText';
import { PositionCopy } from '../exo/positions/collapse-modules/PositionCopy';
import PreviewPositions from '../preview/PreviewPositions';
import DeleteIcon from '@mui/icons-material/Delete';
import { AddInvoice } from './order-invoices/AddInvoice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import MoneyLabel from '../label/MoneyLabel';
import TypeDate from '../form/form-fields/TypeDate';
import TypeNumber from '../form/form-fields/TypeNumber';
import { numberToString } from '../special/numberConverter';
import DateLabel from '../label/DateLabel';
import TypeTaxRate from '../form/form-fields/TypeTaxRate';

const StepOrderInvoives = ({
  totalAmount,
  apiUrl,
  apiFilter = 'status[ne][]=1&status[ne][]=2',
  dataCollection,
  updateDataCollection,
  activeStep,
  setActiveStep,
  step,
  updateValidation,
  button,
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { id, nr, title, recipientType, recipient, startDate } =
    dataCollection.order || {};
  const { apiData } = useFetch(`orders/${id}`);

  const [openAddInvoice, setOpenAddInvoice] = useState(false);

  const { volume, openVolume } = apiData?.data || {};

  useEffect(() => {
    if (apiData && apiData.data.invoices && dataCollection.orderId !== id) {
      updateDataCollection({
        previousInvoices: apiData.data.invoices,
        orderTitle: apiData.data.title,
        orderPrice: volume,
        orderNr: apiData.data.nr,
        orderPositions: apiData.data.positions,
        orderStartDate: apiData.data.startDate,
        orderTaxAmount: apiData.data.taxVolume,
        orderDiscountPercent: apiData.data.discountPercent || 0,
        orderTaxRate: dataCollection?.templateMetaData?.taxRate || 19,
      });
    }
  }, [apiData]);

  const {
    previousInvoices,
    orderTitle,
    orderPositions,
    order,
    showOrderPositions,
    orderPrice,
    orderNr,
    orderDiscountPercent,
    orderStartDate,
    orderTaxAmount,
  } = dataCollection;

  useEffect(() => {
    // validation check
    console.log(orderTitle, orderPrice, orderTaxAmount, orderStartDate);

    if (
      orderTitle &&
      orderPrice &&
      orderNr &&
      orderTaxAmount !== undefined &&
      orderStartDate
    ) {
      updateValidation(true);
    } else {
      updateValidation(false);
    }
  }, [dataCollection]);

  function handleInvoiceChange(newInvoice) {
    const newInvoices = updateOrCreate(previousInvoices || [], newInvoice);
    updateDataCollection({
      previousInvoices: newInvoices,
      previousInvoicesSum: calcSumOfInvoices(newInvoices),
    });
  }

  function handleRemoveInvoice(inv) {
    const newInvoices = previousInvoices.filter((obj) => obj.id !== inv.id);
    updateDataCollection({
      previousInvoices: newInvoices,
      previousInvoicesSum: calcSumOfInvoices(newInvoices),
    });
  }

  function calcSumOfInvoices(invoices) {
    var newSum = 0;
    invoices.forEach((inv) => {
      newSum += inv.amount;
    });
    return newSum;
  }

  return (
    <Box className="w-full h-full flex flex-col gap-2">
      <Typography fontWeight={600}>{t('Order')}</Typography>
      <Order
        dataCollection={dataCollection}
        updateDataCollection={updateDataCollection}
      />
      <Typography fontWeight={600}>{t('Invoices')}</Typography>
      {previousInvoices?.map((inv) => (
        <Invoice
          key={inv.id}
          invoice={inv}
          onChange={handleInvoiceChange}
          onRemove={handleRemoveInvoice}
        />
      ))}
      <ProjectAddElementButton
        addLabel={t('Add Invoice')}
        size="small"
        onClick={() => setOpenAddInvoice(true)}
      />
      <AddInvoice
        open={openAddInvoice}
        onClose={() => setOpenAddInvoice(false)}
        recipientType={recipientType}
        recipientId={recipient?.id}
        onSelect={handleInvoiceChange}
        existingInvoices={previousInvoices}
      />
    </Box>
  );
};

const Order = ({ dataCollection, updateDataCollection }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    orderTitle,
    orderPositions,
    order,
    showOrderPositions,
    orderNr,
    orderPrice,
    orderStartDate,
    orderDiscountPercent,
    orderDiscount,
    orderTaxRate,
  } = dataCollection;

  const { id, nr } = order || {};

  function handleFormFieldChange(name, value) {
    updateDataCollection({ [name]: value });
  }

  function handleOrderPositionsChange(name, value) {
    var newOrderPrice = 0;
    var newOrderTaxAmount;
    value.forEach((element) => {
      const price = parseFloat(element.price) * parseFloat(element.amount);
      newOrderPrice += price;
      newOrderTaxAmount += price * (parseFloat(element.taxRate) / 100);
    });

    updateDataCollection({
      [name]: value,
      orderPrice: newOrderPrice,
      orderTaxAmount: newOrderTaxAmount,
    });
  }

  function handleDiscountFormFieldChange(name, value) {
    // calc new discounts
    const newOrderDiscount = (value / 100) * orderPrice;
    updateDataCollection({ [name]: value, orderDiscount: newOrderDiscount });
  }

  function handlePriceOrTaxChangeFieldChange(name, value) {
    var newData = {};

    const floatValue = parseFloat(value);

    if (name[0] == 'orderTaxRate') {
      newData = {
        orderTaxRate: floatValue,
        orderTaxAmount: orderPrice * (floatValue / 100),
      };
    }
    if (name[0] == 'orderPrice') {
      newData = {
        orderPrice: floatValue,
        orderTaxAmount: floatValue * (orderTaxRate / 100),
      };
    }
    updateDataCollection(newData);
  }

  useEffect(() => {
    calcSum();
  }, [orderPositions]);

  function calcSum() {
    if (orderPositions && orderPositions.length > 0) {
      var newSum = 0;
      orderPositions.forEach((element) => {
        const price = parseFloat(element.price) * parseFloat(element.amount);
        newSum += price;
      });
      if (newSum !== orderPrice) updateDataCollection({ orderPrice: newSum });
    }
  }

  function handleRemove() {
    updateDataCollection({
      orderTitle: '',
      orderPositions: [],
      order: null,
      showOrderPositions: false,
      orderDiscountPercent: null,
      orderNr: '',
      orderPrice: null,
      previousInvoices: [],
      orderStartDate: '',
      orderDiscount: null,
      orderTaxRate: dataCollection?.templateMetaData?.taxRate || 19,
    });
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.card}`,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="px-3 py-2 rounded-lg flex flex-col gap-2"
    >
      <Box className="flex gap-4 justify-between">
        <Box className="flex flex-col">
          <Box className="flex gap-2 items-start">
            <Typography className="w-full pb-2" variant="h4">
              <span style={{ fontWeight: 600 }}>{orderNr}</span>
              {` -  ${orderTitle || ''}`}
            </Typography>
            <IconButton
              onClick={handleRemove}
              size="small"
              className="delButton"
            >
              <DeleteIcon sx={{ fontSize: '15px' }} />
            </IconButton>
          </Box>
          {orderStartDate && (
            <Typography>
              <DateLabel date={orderStartDate} />
            </Typography>
          )}
        </Box>
        {orderPrice && (
          <Box className="flex flex-col gap-2 w-[180px]">
            <Box>
              <Typography
                fontWeight={600}
                fontSize="16px"
                className="opacity-50"
                textAlign="right"
              >
                <MoneyLabel money={orderPrice} />
              </Typography>
              {Boolean(orderDiscount) && (
                <Typography
                  fontWeight={500}
                  fontSize="14px"
                  className="opacity-50"
                  textAlign="right"
                  color="warning"
                >
                  {`-${numberToString(orderDiscountPercent, 1)}% `}
                  <span className=" italic">
                    (<MoneyLabel money={orderPrice - orderDiscount} />)
                  </span>
                </Typography>
              )}
            </Box>
            <TypeNumber
              field={{
                label: t('Order Discount'),
                key: 'orderDiscountPercent',
                unit: '%',
                decimalPlaces: 1,
              }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleDiscountFormFieldChange}
            />
          </Box>
        )}
      </Box>
      {!id && (
        <Box className="pt-1 grid md:grid-cols-2 lg:grid-cols-4 gap-y-4 gap-1">
          <TypeText
            field={{ label: t('Nr'), key: 'orderNr' }}
            size="small"
            dataCollection={dataCollection}
            updateValidationOnChange={handleFormFieldChange}
          />
          <Box className="md:col-span-2 lg:col-span-3">
            <TypeText
              field={{ label: t('Title'), key: 'orderTitle' }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleFormFieldChange}
            />
          </Box>

          {!showOrderPositions && (
            <>
              <TypeNumber
                field={{ label: t('Tax Rate'), key: 'orderTaxRate', unit: '%' }}
                size="small"
                dataCollection={dataCollection}
                updateValidationOnChange={handlePriceOrTaxChangeFieldChange}
              />

              <TypeMoney
                field={{ label: t('Total Amount'), key: 'orderPrice' }}
                size="small"
                dataCollection={dataCollection}
                updateValidationOnChange={handlePriceOrTaxChangeFieldChange}
              />
            </>
          )}
          <Box>
            <TypeDate
              field={{ label: t('Date'), key: 'orderStartDate' }}
              size="small"
              dataCollection={dataCollection}
              updateValidationOnChange={handleFormFieldChange}
            />
          </Box>
        </Box>
      )}
      {!showOrderPositions ? (
        <Box>
          <Button
            onClick={() => handleFormFieldChange('showOrderPositions', true)}
            variant="contained"
            disableElevation
            color="secondary"
            size="small"
          >
            <RemoveRedEyeIcon className="mr-2" sx={{ fontSize: '16px' }} />
            {t('Show all positions in invoice')}
          </Button>
        </Box>
      ) : (
        <PositionsRendering
          allowEdit={!Boolean(id)}
          positions={orderPositions || []}
          onRemove={() => handleFormFieldChange('showOrderPositions', false)}
          onChange={(name, newPositions) =>
            handleOrderPositionsChange('orderPositions', newPositions)
          }
        />
      )}
    </Box>
  );
};

const Invoice = ({ invoice, onChange, onRemove }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { title, nr, showPositions, positions, allowEdit } = invoice;

  function handleChange(key, value) {
    onChange({ ...invoice, [key]: value });
  }

  const [nettoSum, setNettoSum] = useState(null);

  useEffect(() => {
    calcSum();
  }, [positions]);

  function calcSum() {
    var newSum = 0;
    positions.forEach((element) => {
      const price = parseFloat(element.price);
      newSum += price * element.amount;
    });

    setNettoSum(newSum);
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.card}`,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="px-3 py-2 rounded-lg"
    >
      <Box className="flex w-full justify-between">
        <Box className="flex gap-2 items-start">
          <Typography className="w-full pb-2" variant="h4">
            <span style={{ fontWeight: 600 }}>{nr}</span>
            {` -  ${title}`}
          </Typography>
          <IconButton
            onClick={() => onRemove(invoice)}
            size="small"
            className="delButton"
          >
            <DeleteIcon sx={{ fontSize: '15px' }} />
          </IconButton>
        </Box>

        {nettoSum && (
          <Typography fontWeight={600} fontSize="16px" className="opacity-50">
            <MoneyLabel money={nettoSum} />
          </Typography>
        )}
      </Box>
      {positions ? (
        !showPositions ? (
          <Button
            onClick={() => handleChange('showPositions', true)}
            variant="contained"
            disableElevation
            color="secondary"
            size="small"
          >
            <RemoveRedEyeIcon className="mr-2" sx={{ fontSize: '16px' }} />
            {t('Show all positions in invoice')}
          </Button>
        ) : (
          <PositionsRendering
            positions={positions}
            onChange={handleChange}
            onRemove={() => handleChange('showPositions', false)}
          />
        )
      ) : (
        <></>
      )}
    </Box>
  );
};

const PositionsRendering = ({ allowEdit, onChange, positions, onRemove }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box
      sx={{
        bgcolor: allowEdit ? 'transparent' : colors.card,
        '& .delButton': {
          transition: '0.3s ease',
          bgcolor: colors.error + '30',
          color: colors.error,
          border: `1px solid ${colors.error}80`,
          opacity: 0,
          transform: 'translate(-5px)',
        },
        ':hover': {
          '& .editButton, & .delButton': {
            opacity: 1,
            transform: 'translate(0px)',
          },
        },
      }}
      className="rounded-lg px-0 py-1 relative overflow-hidden"
    >
      <IconButton
        onClick={onRemove}
        size="small"
        className="delButton  z-10 backdrop-blur-sm"
        sx={{
          position: 'absolute',
          left: '5px',
          top: allowEdit ? '7px' : 'unset',
          bottom: allowEdit ? 'unset' : '5px',
        }}
      >
        <DeleteIcon sx={{ fontSize: '15px' }} />
      </IconButton>

      {allowEdit ? (
        <ExoPositions
          onPositionChange={(newPositions) =>
            onChange('positions', newPositions)
          }
          collapseModules={[
            { component: PositionImage, key: 'image', label: 'Image' },
            {
              component: PositionDescription,
              key: 'description',
              label: 'Description',
            },
            {
              component: PositionNumber,
              key: 'taxRate',
              label: 'Tax Rate',
              unit: '%',
              decimalPlaces: 0,
            },
            {
              component: PositionText,
              key: 'unit',
              label: 'Unit',
            },
            {
              component: PositionCopy,
              label: 'Duplicate position',
            },
          ]}
        />
      ) : (
        <PreviewPositions positions={positions} />
      )}
    </Box>
  );
};

export default StepOrderInvoives;
